import React, { useLayoutEffect, useRef, useState, useMemo, useEffect } from 'react';
import * as am5core from '@amcharts/amcharts5';
import FullScreenIcon from '../../features/common/FullScreenIcon/FullScreenIcon';
import { RootState } from '../../store';
import { useSelector } from 'react-redux';
import { handleChartDownloadUtil } from '../../utilities/ExportChart';
import { useAppSelector } from '../../hooks/storeHooks';
import { syncCursor as syncCursorUtil, syncAxes as syncAxesUtil } from '../../utilities/chartHelpers';
import DownloadChart from '../../components/time-series-chart/download/downloadChart';
import EmptyState from '../../components/empty-state/EmptyState';
import { ReactComponent as CloseIcon } from '../../images/close-x-icon.svg';
import { createSplitWellChart } from './SplitChartUtil';
import Loader from '../../features/common/Loader/Loader';
import { ProtectComponent, ProtectAction } from '../../rbac';
import { R, useRBAC } from '../../rbac';
import NoAccessComponent from '../../components/no-access-widget/NoAccessComponent';
import './SplitChart.scss';

interface SplitWellTest {
  Date: string;
  Timestamp: number;
  Oil: number;
  Water: number;
  Gas: number;
  [key: string]: string | number | undefined;
}

type SplitSeriesConfig = {
  name: string;
  field: string;
  units: string;
  splitView: boolean;
  testColor: string;
};
interface SplitTestChartProps {
  splitTestChartData: any;
  widgetName: string;
  dashboardName: string;
}

const SplitWellTestChart: React.FC<SplitTestChartProps> = ({ splitTestChartData, widgetName, dashboardName }) => {
  const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
  const amChartRefs = useRef<any[]>([]);
  const fullscreenChartRoot = useRef<any>(null);

  const [fullscreenChartIndex, setFullscreenChartIndex] = useState<number | null>(null);

  const startDate = useSelector((state: RootState) => state.AssetTrendDataSlice.startDate);
  const endDate = useSelector((state: RootState) => state.AssetTrendDataSlice.endDate);
  const wellId = useAppSelector((state) => state.WellTestIdSlice?.data?.WellId);
  const rawWellTestData = useSelector((state: RootState) => state.WellTestChartDataSlice.data);
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const timeSeriesChartData = useAppSelector((state: any) => state.AssetTrendDataByTagsSlice.data);
  const wellTestDataLoading = useSelector((state: RootState) => state.WellTestChartDataSlice.loading);
  const timeSeriesLoading = useAppSelector((state: any) => state.AssetTrendDataByTagsSlice.loading);

  const parameterData = splitTestChartData?.widgetProperty.widgetProperties.parameterConfig[0]?.parameters;

  const { showTooltip } = chartViewOptions || {};
  const owgChart = ['Oil', 'Water', 'Gas'];

  const { can } = useRBAC();
  const resource = R.widget(dashboardName, widgetName);
  const rPermission = {
    canOgwtChartExportJpg: can(resource, 'ogwtChartExportJpg'),
    canOgwtChartExportPdf: can(resource, 'ogwtChartExportPdf'),
    canOgwtChartExpand: can(resource, 'ogwtChartExpand'),
  };

  const wellTestDataAll: SplitWellTest[] = useMemo(() => {
    if (wellId === undefined || !rawWellTestData || rawWellTestData.length === 0) {
      return [];
    }
    return rawWellTestData.map((item: any) => ({
      Date: item.Date,
      Timestamp: item.Timestamp,
      Oil: item.Oil,
      Water: item.Water,
      Gas: item.Gas,
    }));
  }, [rawWellTestData, wellId]);

  // Transform timeSeriesChartData
  const timeSeriesChartDataTransformed = useMemo(() => {
    if (!Array.isArray(timeSeriesChartData)) return [];
    const grouped: Record<string, any> = {};
    timeSeriesChartData.forEach((item: any) => {
      const date = item.date;
      if (!grouped[date]) {
        grouped[date] = {
          Date: date,
          Timestamp: new Date(date).getTime(),
        };
      }
      // ESP wells
      if (item.paramTypeId === '1') {
        grouped[date].IntakePressure = Number(item.value);
      }
      if (item.paramTypeId === '2') {
        grouped[date].Frequency = Number(item.value);
      }
      // GL wells
      if (item.paramTypeId === '196') {
        grouped[date].InjectionPressure = Number(item.value);
      }
      if (item.paramTypeId === '191') {
        grouped[date].GasInjectionRate = Number(item.value);
      }
    });
    return Object.values(grouped);
  }, [timeSeriesChartData]);

  const splitSeriesConfig: SplitSeriesConfig[] = useMemo(() => {
    if (!parameterData) return [];

    return parameterData.map(
      (param: any): SplitSeriesConfig => ({
        name: param.description,
        field: param.description?.replace(/\s*\(.*?\)/, '').replace(/\s+/g, ''),
        units: param.units,
        splitView: true,
        testColor: param.lineColor,
      }),
    );
  }, [parameterData]);

  // Merge both data sources
  const mergedWellTestDataAll: SplitWellTest[] = useMemo(() => {
    return [...wellTestDataAll, ...timeSeriesChartDataTransformed];
  }, [wellTestDataAll, timeSeriesChartDataTransformed]);

  const wellTestData = useMemo(() => {
    const startMs = startDate ? new Date(startDate).getTime() : undefined;
    const endMs = endDate ? new Date(endDate).getTime() : undefined;
    return mergedWellTestDataAll.filter((item) => {
      const timestampMs = item.Timestamp;
      if (startMs && endMs) {
        return timestampMs >= startMs && timestampMs <= endMs;
      }
      return true;
    });
  }, [mergedWellTestDataAll, startDate, endDate]);

  // Cursor and Axes Sync
  const syncCursor = syncCursorUtil.bind(null, amChartRefs);
  const syncAxes = syncAxesUtil.bind(null, amChartRefs);

  const addAmChartRef = (index: number, chart: any) => {
    amChartRefs.current[index] = chart;
  };

  // Chart rendering for each series
  useLayoutEffect(() => {
    splitSeriesConfig
      .filter((cfg: SplitSeriesConfig) => cfg.splitView)
      .forEach((config: SplitSeriesConfig, idx: number) => {
        if (fullscreenChartIndex === idx) return;
        const chartDiv = chartRefs.current[idx];
        if (!chartDiv) return;
        createSplitWellChart({
          chartDiv,
          config,
          wellTestData,
          startDate: startDate instanceof Date ? startDate.toISOString() : (startDate ?? undefined),
          endDate: endDate instanceof Date ? endDate.toISOString() : (endDate ?? undefined),
          showTooltip,
          addAmChartRef,
          idx,
          syncCursor,
          syncAxes,
          isFullscreen: false,
        });
      });

    // Cleanup
    return () => {
      chartRefs.current.forEach((ref, idx) => {
        if (ref && (ref as any)['__am5Root'] && fullscreenChartIndex !== idx) {
          try {
            ((ref as any)['__am5Root'] as am5core.Root).dispose();
          } catch (e) {
            console.error('Error disposing chart root:', e);
          }
        }
      });
    };
  }, [wellTestData, chartViewOptions, showTooltip, startDate, endDate, fullscreenChartIndex, splitSeriesConfig]);

  // Fullscreen chart rendering
  useEffect(() => {
    if (fullscreenChartIndex === null) return;

    const config = splitSeriesConfig.filter((cfg) => cfg.splitView)[fullscreenChartIndex];
    if (!config) return;

    const chartDiv = document.getElementById('fullscreen-chart-container');
    if (!chartDiv) return;

    fullscreenChartRoot.current = createSplitWellChart({
      chartDiv,
      config,
      wellTestData,
      startDate: startDate instanceof Date ? startDate.toISOString() : (startDate ?? undefined),
      endDate: endDate instanceof Date ? endDate.toISOString() : (endDate ?? undefined),
      showTooltip,
      isFullscreen: true,
    });

    // Cleanup
    return () => {
      if (fullscreenChartRoot.current) {
        try {
          fullscreenChartRoot.current.dispose();
        } catch {
          console.error('Error disposing fullscreen chart root');
        }
        fullscreenChartRoot.current = null;
      }
      const chartDiv = document.getElementById('fullscreen-chart-container');
      if (chartDiv && (chartDiv as any)['__am5Root']) {
        try {
          ((chartDiv as any)['__am5Root'] as am5core.Root).dispose();
        } catch {
          console.error('Error creating chart in fullscreen');
        }
      }
    };
  }, [fullscreenChartIndex, wellTestData, chartViewOptions, showTooltip, startDate, endDate, splitSeriesConfig]);

  const VISIBLE_CONFIG = splitSeriesConfig.filter((cfg) => cfg.splitView);

  const handleFullscreen = (idx: number) => {
    setFullscreenChartIndex(idx);

    setTimeout(() => {
      const fullscreenModal = document.querySelector('.fullscreen-modal');
      if (fullscreenModal && !document.fullscreenElement) {
        fullscreenModal.requestFullscreen().catch((err) => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    }, 0);
  };

  const handleCloseFullscreen = () => {
    setFullscreenChartIndex(null);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error(`Error attempting to exit fullscreen: ${err.message}`);
      });
    }
  };

  const handleChartDownload = (option: string, idx: number) => {
    const config = VISIBLE_CONFIG[idx];
    const fileName = `${config.name} (${config.units})_SplitChart`;
    const header = `${config.name} (${config.units})`;

    handleChartDownloadUtil(option, idx, fullscreenChartIndex, fileName, header);
  };

  if (wellTestDataLoading || timeSeriesLoading) {
    return <Loader />;
  }

  return (
    <div className='split-ogl-chart-container'>
      <div className='flex flex-col gap-[3px] items-start self-stretch'>
        {VISIBLE_CONFIG.map((config, idx) => {
          const hasData = wellTestData.some((item) => item[config.field] !== undefined && item[config.field] !== null);
          return (
            <section key={config.name} className={`chart-section ${config.name}`}>
              <ProtectComponent
                resource={R.widget(dashboardName, widgetName)}
                action='ogwtChartView'
                disable={config.name !== 'Oil' && config.name !== 'Water' && config.name !== 'Gas'}
                fallback={
                  <div className={`flex w-full flex-col`}>
                    <h2 style={{ fontSize: '16px', fontWeight: 600 }}>{config.name}</h2>
                    <div className={`p-2`}>
                      <NoAccessComponent
                        message={`You do not currently have access to view ${config.name} graph, please contact your site Administrator.`}
                      />
                    </div>
                  </div>
                }
              >
                {hasData ? (
                  <div className='flex w-full flex-col justify-center items-center'>
                    <div className='flex w-full items-center justify-between mb-1'>
                      <div className='split-chart-title'>
                        {config.name}
                        {config.units && config.units !== '' ? ` (${config.units})` : ''}
                      </div>
                      <div className='flex items-center gap-4'>
                        <ProtectAction
                          canAccess={owgChart.includes(config.name) ? rPermission.canOgwtChartExpand : true}
                        >
                          <button className='chart-action-button' onClick={() => handleFullscreen(idx)}>
                            <FullScreenIcon />
                          </button>
                        </ProtectAction>
                        <DownloadChart
                          onClick={(option) => handleChartDownload(option, idx)}
                          canExportJpg={owgChart.includes(config.name) ? rPermission.canOgwtChartExportJpg : true}
                          canExportPdf={owgChart.includes(config.name) ? rPermission.canOgwtChartExportPdf : true}
                        />
                      </div>
                    </div>
                    <div className='w-full flex flex-col justify-center items-end'>
                      <div id={`chart-wrapper-${idx}`} className='w-full'>
                        <div className='w-full h-[181px] flex'>
                          <div
                            ref={(el) => (chartRefs.current[idx] = el)}
                            id={`ogl-chart-content-${idx}`}
                            className='split-chart-container'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='chart-section__axis-label'>
                      <span className='chart-section__axis-label-first'>
                        {config.name}
                        {config.units && config.units !== '' ? ` (${config.units})` : ''}
                      </span>
                    </div>
                    <EmptyState />
                  </>
                )}
              </ProtectComponent>

              {/* Fullscreen Modal */}
              {fullscreenChartIndex === idx && (
                <div className='fullscreen-modal'>
                  <div className='fullscreen-header'>
                    <div className='fullscreen-header-components'>
                      <span className='fullcreen-header-content'>
                        {config.name} ({config.units})
                      </span>
                      <button className='close-button' onClick={handleCloseFullscreen}>
                        <CloseIcon />
                      </button>
                    </div>
                    <div className='min-max flex justify-between items-center gap-[15px]'>
                      <DownloadChart
                        onClick={(option) => handleChartDownload(option, idx)}
                        canExportJpg={owgChart.includes(config.name) ? rPermission.canOgwtChartExportJpg : true}
                        canExportPdf={owgChart.includes(config.name) ? rPermission.canOgwtChartExportPdf : true}
                      />
                    </div>
                  </div>
                  <div id='fullscreen-chart-container' className='fullscreen-chart-container' />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(SplitWellTestChart);
